import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import { AssetsState } from '@webapp/store/assets/state';
import { trackList } from '@webapp/store/tracks/selectors';
import { assetsList } from '@webapp/store/assets/selectors';
import { CreateDialog } from '@webapp/store/ui/action-creators';
import { RemoveTrack } from '@webapp/store/tracks/action-creators';
import { UpdateTrackOrder } from '@webapp/store/tracks/action-creators';

// Types
import { Release } from '@common/types/release/release';
import SimpleTrack from '@common/types/release/simple-track';
import { ReleaseType } from '@common/types/release/release-type';
import { SimpleRelease } from '@common/types/release/simple-release';

import { AssetUpload } from '@webapp/types/assets/asset-upload';
import { DialogConfiguration } from '@webapp/types/ui/dialog-configuration';

// Utils
import { normalizeTrackName } from '@webapp/utils/normalizers';

// Services
import { TrackOrderUpdate } from '@services/track/types/track-order-update';
import { AssetValidationResults } from '@services/asset/types/asset-validation-results';

// Controllers
import SidebarCtrl from '@webapp/controllers/sidebar';

// Componentes
import AddSong from '@components/upload/add-song';
import Typography from '@components/basics/typography';
import TrackSidebarItems from '../track-sidebar-items';
import { ButtonType } from '@components/actions/button/index';
import { assetOwnersById } from '@webapp/store/ownership/selectors';
import { Ownership } from '@services/ownership/types/ownership';

interface TrackSidebarProps {
  className?: string;
  release: Partial<SimpleRelease>;
  tracks: Partial<SimpleTrack>[];
  statuses: AssetsState['statuses'];
  assets: AssetUpload[];
  titles: { id?: string; title: string }[];
  type?: ReleaseType;
  artistId?: string;
  disabled?: boolean;
  ownerShipAssets: {
    [assetId: string]: {
      [ownershipId: string]: Ownership;
    };
  };
  location: any;
  updateOrder: (
    order: TrackOrderUpdate[],
    releaseId: string
  ) => UpdateTrackOrder;
  openModal: (trackId: string, callback: (value: any) => void) => CreateDialog;
  removeTrack: (releaseId: string, trackId: string) => RemoveTrack;
  goBack: () => CallHistoryMethodAction;

  disableRemove?: boolean;
  disableAddMore?: boolean;
  disableSorting?: boolean;
  routePrefix?: string;
}

const buildDialog = (
  callback: (value: any) => void
): DialogConfiguration<ButtonType> => {
  return {
    title: 'Are you sure you want to delete this song?',
    text: "This can't be undone.",
    actions: [
      { type: 'primary', label: 'No', value: false },
      { type: 'secondary', label: 'Yes', value: true },
    ],
    callback,
  };
};

const mapStateToProps = (state: AppState) => {
  const tracks = trackList(
    state.tracks.tracks,
    state.release.release?.id as string
  );

  return {
    release: state.release.release as Partial<Release>,
    tracks,
    statuses: state.assets.statuses,
    titles: tracks.map((track) => ({
      id: track.id,
      title: normalizeTrackName(
        {
          ...track,
          trackVersion:
            track.trackVersion === 'Original' ? '' : track.trackVersion,
        },
        state
      ),
    })),
    assets: assetsList(state.assets),
    type: state.release.release?.type,
    artistId: state.artist.active?.id,
    ownerShipAssets: state.ownership.assets,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateOrder: (order: TrackOrderUpdate[], releaseId: string) =>
    dispatch(new UpdateTrackOrder(order, releaseId)),
  openModal: (trackId: string, callback: (value: any) => void) =>
    dispatch(new CreateDialog(trackId, buildDialog(callback))),
  removeTrack: (releaseId: string, trackId: string) =>
    dispatch(new RemoveTrack(releaseId, trackId)),
  goBack: () => dispatch(push('../title')),
});

export const TrackSidebar: React.FC<TrackSidebarProps> = (props) => {
  const showAddSong = () => {
    return !(
      props.location?.pathname.endsWith('title') || props.disableAddMore
    );
  };

  const mergedTracks = React.useMemo(
    () =>
      props.tracks.map((track) => {
        const asset = props.assets.find((a) => a.id === track.id);
        const title = props.titles.find((t) => t.id === track.id);
        const status = track.id ? props.statuses[track.id] : undefined;
        let isOwnerShipValid = false;
        if (track.id) {
          const ownerShipOwner = assetOwnersById(
            props.ownerShipAssets,
            track.id,
            'OWNER'
          );
          const ownerShipWriter = assetOwnersById(
            props.ownerShipAssets,
            track.id,
            'WRITER'
          );
          if (
            ownerShipOwner.reduce(
              (memo, owner) => memo + owner.split * 10 ** 10,
              0
            ) /
              10 ** 10 ===
              100 &&
            ownerShipWriter.reduce(
              (memo, owner) => memo + owner.split * 10 ** 10,
              0
            ) /
              10 ** 10 ===
              100
          ) {
            isOwnerShipValid = true;
          } else {
            isOwnerShipValid = false;
          }
        }

        return [
          track,
          asset,
          title?.title || track.title || '',
          status,
          isOwnerShipValid,
        ] as [
          SimpleTrack,
          AssetUpload | undefined,
          string,
          AssetValidationResults | undefined,
          boolean
        ];
      }),
    [
      props.tracks,
      props.ownerShipAssets,
      props.assets,
      props.statuses,
      props.titles,
    ]
  );

  const handleRemove = (trackId: string, isActive: boolean) => {
    props.openModal(trackId, (confirmation) => {
      if (confirmation) {
        if (isActive) {
          props.goBack();
        }
        props.removeTrack(props.release.id as string, trackId);
      }
    });
  };

  return (
    <SidebarCtrl
      className={props.className}
      title={<Translate id="RELEASE.SIDEBAR.LIST.TRACKLIST" />}
    >
      {!props.tracks.length ? (
        <NoTracks weight="400" type="small">
          <Translate id="RELEASE.SIDEBAR.LIST.NO_TRACKS" />
        </NoTracks>
      ) : null}
      <TrackSidebarItems
        tracks={mergedTracks}
        releaseId={props.release?.id}
        artistId={props.artistId}
        location={props.location}
        disabled={props.disabled}
        updateOrder={(tracks) =>
          props.updateOrder(tracks, props.release?.id || '')
        }
        onRemove={handleRemove}
        hideRemoveOption={!!props.release?.source}
        disableRemove={props.disableRemove || !!props.release?.source}
        disableSorting={props.disableSorting || !!props.release?.source}
        routePrefix={props.routePrefix}
      />
      {showAddSong() ? (
        <AddSong
          locked={
            props.release?.backofficeStatus === 'accepted' ||
            props.release?.isPublished
          }
          releaseId={props.release?.id}
        />
      ) : null}
    </SidebarCtrl>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(TrackSidebar);

const NoTracks = styled(Typography)`
  color: ${({ theme }) => theme.colors.tertiary};
`;
